'use client';

import { c as _c } from "react/compiler-runtime";
import { Vehicle, type Vendor } from '@cruz-com/lookup';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import _ from 'lodash';
import Link from 'next/link';
import { useState } from 'react';
import { HiMinus, HiOutlineSquares2X2, HiPlus } from 'react-icons/hi2';
import { SiGooglemaps } from 'react-icons/si';
import { VscCallOutgoing } from 'react-icons/vsc';
import { twMerge } from 'tailwind-merge';
import { vendorApi } from '@/api';
import { RatingStars } from '@/components';
import { formatPhone, LeadTypeEnum, lkpLeadList, trackingLocationPhoneClick } from '@/lib';
import { useGlobalStore } from '@/store/provider';
import { ToggleSaveVehicle } from '../vehicle/toggleSaveVehicle';
type Props = {
  id?: string;
  vehicle?: Vehicle.Summary;
  initialData?: Vendor.Location | undefined;
  showLinkToLocation?: boolean;
  showMessageDealer?: boolean;
  showRequestHistoryReport?: boolean;
};
const navStyle = twMerge('nav-item', 'h-11');

// dealer on left side
export const DealerLocationSide = t0 => {
  const $ = _c(83);
  const {
    id,
    vehicle,
    initialData,
    showLinkToLocation,
    showMessageDealer,
    showRequestHistoryReport
  } = t0;
  const {
    setLead
  } = useGlobalStore(_temp);
  const t1 = `DealerLocation_${id}`;
  let t2;
  if ($[0] !== id || $[1] !== t1) {
    t2 = [t1, id];
    $[0] = id;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== id) {
    t3 = () => vendorApi.getLocation(id as string);
    $[3] = id;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== initialData || $[6] !== t2 || $[7] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3,
      staleTime: 1000,
      initialData,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false
    };
    $[5] = initialData;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const {
    data
  } = useQuery(t4);
  let t5;
  if ($[9] !== data) {
    t5 = data || {};
    $[9] = data;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const {
    name,
    address1,
    city,
    state_abbr,
    zip_code,
    distance,
    phone,
    place,
    vehicle_count,
    dealer_url
  } = t5;
  let t6;
  if ($[11] !== place) {
    t6 = place || {};
    $[11] = place;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const {
    google_map_uri,
    rating,
    hours
  } = t6;
  if (_.isEmpty(data)) {
    return null;
  }
  let t7;
  if ($[13] !== hours || $[14] !== place?.overview || $[15] !== rating) {
    t7 = () => {
      if (!_.isEmpty(place?.overview) || rating || false || !_.isEmpty(hours)) {
        return true;
      }
      return false;
    };
    $[13] = hours;
    $[14] = place?.overview;
    $[15] = rating;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const showInfo = t7;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t8;
  let t9;
  if ($[17] !== address1 || $[18] !== city || $[19] !== dealer_url || $[20] !== distance || $[21] !== google_map_uri || $[22] !== hours || $[23] !== id || $[24] !== name || $[25] !== phone || $[26] !== place || $[27] !== rating || $[28] !== setLead || $[29] !== showInfo || $[30] !== showLinkToLocation || $[31] !== showMessageDealer || $[32] !== showRequestHistoryReport || $[33] !== state_abbr || $[34] !== vehicle?._id || $[35] !== vehicle?.title || $[36] !== vehicle_count || $[37] !== zip_code) {
    const contactLeadTypeList = showRequestHistoryReport ? lkpLeadList : _.filter(lkpLeadList, _temp2);
    const renderMessageDealer = () => <section className="relative z-10 mt-6 overflow-hidden rounded-lg border bg-zinc-800 py-2 dark:bg-zinc-800" aria-labelledby="contact-seller-heading"><h2 id="contact-seller-heading" className="px-6 pt-2! text-xl/7! font-semibold! text-white dark:font-medium! dark:text-zinc-300">Contact Seller<div className="w-6 border-b-3 border-red-600 pt-1" aria-hidden="true" /></h2><ul className="btn-group mx-2 bg-white dark:bg-zinc-900" aria-label="Contact Seller Options">{_.map(contactLeadTypeList, t15 => {
          const {
            id: id_0,
            name: name_0
          } = t15;
          return <li key={`lead_${id_0}`}><button type="button" aria-label={`Send a ${name_0} request to the dealer`} onClick={() => setLead({
              type: id_0,
              vehicle_id: vehicle?._id as string || "",
              vehicle_title: vehicle?.title || ""
            })} className={navStyle}>{name_0}</button></li>;
        })}</ul></section>;
    t8 = "relative max-w-[260px]";
    t9 = "dealer-info-heading";
    if ($[45] !== name) {
      t10 = <h2 id="dealer-info-heading" className="text-lg/6! font-bold dark:font-medium pb-0!">{name}</h2>;
      $[45] = name;
      $[46] = t10;
    } else {
      t10 = $[46];
    }
    if ($[47] !== address1 || $[48] !== city || $[49] !== distance || $[50] !== place || $[51] !== state_abbr || $[52] !== zip_code) {
      t11 = <address className="text-sm/6 not-italic" aria-label="Dealer Address"><Address place={place} address1={address1} city={city} state_abbr={state_abbr} zip_code={zip_code} distance={distance} /></address>;
      $[47] = address1;
      $[48] = city;
      $[49] = distance;
      $[50] = place;
      $[51] = state_abbr;
      $[52] = zip_code;
      $[53] = t11;
    } else {
      t11 = $[53];
    }
    if ($[54] !== hours || $[55] !== place?.overview || $[56] !== rating || $[57] !== showInfo) {
      t12 = showInfo() && <section className="mt-2 space-y-3" aria-label="Dealer Overview"><Ratings rating={rating || 0} /><Hours hours={hours || []} />{!_.isEmpty(place?.overview) && <div className="text-sm/6">{place?.overview}</div>}</section>;
      $[54] = hours;
      $[55] = place?.overview;
      $[56] = rating;
      $[57] = showInfo;
      $[58] = t12;
    } else {
      t12 = $[58];
    }
    let t16;
    if ($[59] !== id || $[60] !== phone) {
      t16 = phone && <li><a href={`tel:${phone}`} onClick={() => trackingLocationPhoneClick(id as string)} className={navStyle} aria-label={`Call the dealer at ${formatPhone(phone)}`}><NavItem><VscCallOutgoing aria-hidden="true" className="size-4" />Call {formatPhone(phone)}</NavItem></a></li>;
      $[59] = id;
      $[60] = phone;
      $[61] = t16;
    } else {
      t16 = $[61];
    }
    let t17;
    if ($[62] !== google_map_uri) {
      t17 = google_map_uri && <li><a href={google_map_uri} target="_blank" className={navStyle} aria-label="Open dealer location on Google Maps"><NavItem><SiGooglemaps aria-hidden="true" className="size-4" />Google Maps</NavItem></a></li>;
      $[62] = google_map_uri;
      $[63] = t17;
    } else {
      t17 = $[63];
    }
    let t18;
    if ($[64] !== dealer_url || $[65] !== showLinkToLocation || $[66] !== vehicle_count) {
      t18 = showLinkToLocation && <li><Link hrefLang="en" href={dealer_url || ""} className={navStyle} aria-label="View dealer inventory"><NavItem><div className="flex items-center gap-x-3"><HiOutlineSquares2X2 aria-hidden="true" className="size-4" />Dealer's Inventory</div><div className="flex items-center"><span className="sr-only">, vehicle count: </span><span className="nav-count">{(vehicle_count || 0).toLocaleString()}</span></div></NavItem></Link></li>;
      $[64] = dealer_url;
      $[65] = showLinkToLocation;
      $[66] = vehicle_count;
      $[67] = t18;
    } else {
      t18 = $[67];
    }
    if ($[68] !== t16 || $[69] !== t17 || $[70] !== t18) {
      t13 = <ul className="btn-group mt-4" aria-label="Quick Actions">{t16}{t17}{t18}</ul>;
      $[68] = t16;
      $[69] = t17;
      $[70] = t18;
      $[71] = t13;
    } else {
      t13 = $[71];
    }
    t14 = showMessageDealer && renderMessageDealer();
    $[17] = address1;
    $[18] = city;
    $[19] = dealer_url;
    $[20] = distance;
    $[21] = google_map_uri;
    $[22] = hours;
    $[23] = id;
    $[24] = name;
    $[25] = phone;
    $[26] = place;
    $[27] = rating;
    $[28] = setLead;
    $[29] = showInfo;
    $[30] = showLinkToLocation;
    $[31] = showMessageDealer;
    $[32] = showRequestHistoryReport;
    $[33] = state_abbr;
    $[34] = vehicle?._id;
    $[35] = vehicle?.title;
    $[36] = vehicle_count;
    $[37] = zip_code;
    $[38] = t10;
    $[39] = t11;
    $[40] = t12;
    $[41] = t13;
    $[42] = t14;
    $[43] = t8;
    $[44] = t9;
  } else {
    t10 = $[38];
    t11 = $[39];
    t12 = $[40];
    t13 = $[41];
    t14 = $[42];
    t8 = $[43];
    t9 = $[44];
  }
  let t15;
  if ($[72] !== vehicle) {
    t15 = vehicle && vehicle.sale_status_id !== Vehicle.SaleStatusEnum.Sold && <section className="mt-5" aria-labelledby="save-vehicle-heading"><h2 id="save-vehicle-heading" className="sr-only">Save Vehicle Option</h2><div className="pb-2 text-sm font-light"><strong className="font-medium">Save this vehicle</strong> for easy access, comparison, and to receive notifications when the price changes or it's sold</div><ToggleSaveVehicle _id={vehicle._id as string} buttonStyle="w-full" /></section>;
    $[72] = vehicle;
    $[73] = t15;
  } else {
    t15 = $[73];
  }
  let t16;
  if ($[74] !== t10 || $[75] !== t11 || $[76] !== t12 || $[77] !== t13 || $[78] !== t14 || $[79] !== t15 || $[80] !== t8 || $[81] !== t9) {
    t16 = <aside className={t8} aria-labelledby={t9}>{t10}{t11}{t12}{t13}{t14}{t15}</aside>;
    $[74] = t10;
    $[75] = t11;
    $[76] = t12;
    $[77] = t13;
    $[78] = t14;
    $[79] = t15;
    $[80] = t8;
    $[81] = t9;
    $[82] = t16;
  } else {
    t16 = $[82];
  }
  return t16;
};
const NavItem = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="flex w-full items-center gap-x-3 py-0.5">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};

// address
const Address = t0 => {
  const $ = _c(15);
  const {
    place,
    city,
    state_abbr,
    zip_code,
    distance
  } = t0;
  let t1;
  if ($[0] !== distance || $[1] !== place) {
    t1 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const {
        full_address
      } = place || {};
      if (full_address) {
        const x = full_address.split(", ");
        const city_state = _.takeRight(x, 2).join(", ");
        let t2;
        if ($[3] !== distance) {
          t2 = distance && `(${distance.toLocaleString()} mi. away)`;
          $[3] = distance;
          $[4] = t2;
        } else {
          t2 = $[4];
        }
        let t3;
        if ($[5] !== city_state || $[6] !== t2) {
          t3 = <>{city_state} {t2}</>;
          $[5] = city_state;
          $[6] = t2;
          $[7] = t3;
        } else {
          t3 = $[7];
        }
        t1 = t3;
        break bb0;
      }
    }
    $[0] = distance;
    $[1] = place;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  if (t1 !== Symbol.for("react.early_return_sentinel")) {
    return t1;
  }
  let t2;
  if ($[8] !== distance) {
    t2 = distance && `(${distance.toLocaleString()} mi. away)`;
    $[8] = distance;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== city || $[11] !== state_abbr || $[12] !== t2 || $[13] !== zip_code) {
    t3 = <>{city}, {state_abbr} {zip_code} {t2}</>;
    $[10] = city;
    $[11] = state_abbr;
    $[12] = t2;
    $[13] = zip_code;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  return t3;
};
const Ratings = t0 => {
  const $ = _c(2);
  const {
    rating
  } = t0;
  if (!rating || rating < 3) {
    return null;
  }
  let t1;
  if ($[0] !== rating) {
    t1 = <div className="flex items-center gap-2 font-medium"><div className="text-sm">{rating}</div><RatingStars rating={rating} size={5} /></div>;
    $[0] = rating;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const Hours = t0 => {
  const $ = _c(16);
  const {
    hours
  } = t0;
  const [showAllHours, setShowAllHours] = useState(false);
  if (_.isEmpty(hours)) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = dayjs().isoWeekday();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const wdIdx = t1 - 1;
  let t2;
  if ($[1] !== wdIdx) {
    t2 = (d, idx) => {
      const x = d.split(": ");
      return <li key={`hour_${idx}`} className={twMerge("flex items-center justify-between px-4 py-2 text-sm", wdIdx === idx && "font-medium")}><span>{x[0]}</span><span>{x[1]}</span></li>;
    };
    $[1] = wdIdx;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const renderHour = t2;
  let t3;
  if ($[3] !== hours || $[4] !== wdIdx) {
    t3 = () => {
      const today = hours[wdIdx];
      const x_0 = today ? today.split(": ") : [];
      return <button type="button" onClick={() => setShowAllHours(true)} className="btn-outline flex w-full items-center justify-between rounded px-3 py-2 text-sm font-medium"><div className="text-left"><span className="text-xs font-light">Today's Sales Hours</span><div>{_.last(x_0) || "Closed"}</div></div><HiPlus className="size-4" /></button>;
    };
    $[3] = hours;
    $[4] = wdIdx;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const renderTodayHours = t3;
  if (!showAllHours) {
    let t4;
    if ($[6] !== renderTodayHours) {
      t4 = renderTodayHours();
      $[6] = renderTodayHours;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    return t4;
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setShowAllHours(false);
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <li><button type="button" onClick={t4} className="flex w-full items-center justify-between rounded-t-sm bg-zinc-50 p-3 font-normal dark:bg-transparent"><span className="text-sm font-medium">Sales Hours</span><HiMinus className="size-4" /></button></li>;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== hours || $[11] !== renderHour || $[12] !== showAllHours) {
    t6 = showAllHours && _.map(hours, (item, idx_0) => renderHour(item, idx_0));
    $[10] = hours;
    $[11] = renderHour;
    $[12] = showAllHours;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t6) {
    t7 = <ul className="divide-y rounded border border-zinc-300 dark:border-zinc-700">{t5}{t6}</ul>;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
};
function _temp(state) {
  return state;
}
function _temp2(x) {
  return x.id !== LeadTypeEnum.RequestHistoryReport;
}